import { useTranslation } from 'react-i18next';
import { enablePush, getPushPermissionState } from '../../lib/fcm';

/**
 * Notification-status card on the /profile screen. 3-state state machine
 * driven by getPushPermissionState():
 *
 *  - granted   → passive "on" message, no CTA
 *  - default   → CTA "Turn on" button → enablePush()
 *  - denied    → blocked title + browser-settings instruction (no CTA, since
 *                browser permission can't be re-requested from JS)
 *  - unsupported → component returns null
 *
 * Phase E extends this component with per-category opt-out + quiet hours.
 */
export function PushStatusCard() {
  const { t } = useTranslation('common');
  const state = getPushPermissionState();

  if (state === 'unsupported') return null;

  if (state === 'granted') {
    return (
      <Card>
        <BellIcon variant="on" />
        <p className="flex-1 text-sm text-[var(--color-text)]">
          {t('push.profile.on', { defaultValue: 'Trip notifications are on' })}
        </p>
      </Card>
    );
  }

  if (state === 'denied') {
    return (
      <Card>
        <BellIcon variant="off" />
        <div className="flex-1">
          <p className="text-sm font-medium text-[var(--color-text)]">
            {t('push.profile.deniedTitle', { defaultValue: 'Notifications are blocked' })}
          </p>
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">
            {t('push.profile.deniedBody', { defaultValue: 'To turn them back on, allow notifications for this site in your browser settings.' })}
          </p>
        </div>
      </Card>
    );
  }

  // state === 'default'
  return (
    <Card>
      <BellIcon variant="default" />
      <p className="flex-1 text-sm text-[var(--color-text)]">
        {t('push.profile.cta', { defaultValue: 'Get notified when a new trip is allocated' })}
      </p>
      <button
        type="button"
        onClick={() => { void enablePush(); }}
        className="rounded-full bg-[var(--color-btn-primary,#1B6A41)] px-4 py-1.5 text-sm font-medium text-white"
      >
        {t('push.card.enable', { defaultValue: 'Turn on' })}
      </button>
    </Card>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  // Match InstallCard's "mb-4" sibling-spacing convention; no horizontal margin
  // (parent ProfileRoute container owns side padding).
  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-2xl border border-[var(--color-border,#d1d5db)] bg-[var(--color-surface)] px-4 py-3"
      data-testid="push-status-card"
    >
      {children}
    </div>
  );
}

function BellIcon({ variant }: { variant: 'on' | 'off' | 'default' }) {
  if (variant === 'off') {
    return (
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
        <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
        <path d="M18 8a6 6 0 0 0-9.33-5" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
